import { useCallback, useEffect, useState, useMemo } from 'react';

import { useBxpWebStargateClient } from '@wac-web/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { checkFeatureFlagGate } from '@wac-web/utils/feature-flags/check-feature-flag';
import { featureFlags } from '@wac-web/utils/feature-flags/feature-flag-manifest';
import { useLocation, useNavigate } from 'react-router-dom';

import {
	BASE_URL,
	FASTSHIFT_DOMAINS,
	FASTSHIFT_BASE_URL,
	FASTSHIFT_AUTH_REDIRECT_URL,
} from '@/common/routes/constants';
import { removeTrailingSlash } from '@/common/routes/utils';
import { CONSENT_PAGE_LEGAL_DISPLAY_TEXT_VERSION } from '@/pages/legal-consent';
import { useFastshiftDispatch, useFastshiftSelector } from '@/state';
import * as StateActions from '@/state/actions';

import { useRoutes, useUserInfo } from '../../../hooks';
import {
	canUserAccessRoutePolicyByUserType,
	FASTSHIFT_ROUTE_ACCESS_POLICIES,
	getRouteKeyForPathname,
	routeRequiresMoveTicketAccess,
} from '../route-access-policy';

export const UserAccessValidationState = {
	LOADING_PORTAL_ELIGIBILITY: 'LOADING_PORTAL_ELIGIBILITY',
	LOADING_USER_PERMISSIONS: 'LOADING_USER_PERMISSIONS',
	VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE: 'VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE',
	VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL: 'VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL',
	VALIDATION_FAIL_CUSTOMER_NOT_CONSENTED: 'VALIDATION_FAIL_CUSTOMER_NOT_CONSENTED',
	VALIDATION_SUCCESS: 'VALIDATION_SUCCESS',
	VALIDATION_SUCCESS_GENERAL_ACCESS: 'VALIDATION_SUCCESS_GENERAL_ACCESS',
} as const;

export type UserAccessValidationState =
	(typeof UserAccessValidationState)[keyof typeof UserAccessValidationState];

export const usePortalAuthenticationAndAccessControl = () => {
	const isGeneralAccessEnabled = checkFeatureFlagGate(featureFlags.sxpFastshiftEnableGeneralAccess);
	const stargateClient = useBxpWebStargateClient();
	const { isValidFastshiftRoute, domainId, paths, isValidFastshiftPartnerRoute } = useRoutes();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const dispatch = useFastshiftDispatch();
	const { data: user, error: userError, pending: userIsPending } = useUserInfo();

	const { data: customerDomainData, error: customerDomainError } = useFastshiftSelector(
		(state) => state.customerDomain,
	);
	const { data: domainsForPartnerAccess } = useFastshiftSelector((state) => state.customerDomains);

	const { data: userHasConsented, pending: userHasConsentedIsPending } = useFastshiftSelector(
		(state) => state.portalUserConsent,
	);

	const [userAccessState, setUserAccessState] = useState<UserAccessValidationState>(
		UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY,
	);

	const strippedPathname = useMemo(() => removeTrailingSlash(pathname), [pathname]);

	const isPartnerOrInternal = user?.userType === 'PARTNER' || user?.userType === 'INTERNAL';

	const pageDomainIsPresentInPartnerSelectionTable = useMemo(
		() =>
			domainsForPartnerAccess?.data?.some(
				(accessibleDomain) => accessibleDomain.customerDomainId === domainId,
			),
		[domainsForPartnerAccess, domainId],
	);

	const shouldShowSideNav: boolean = useMemo(() => {
		return Boolean(
			(
				[
					UserAccessValidationState.VALIDATION_SUCCESS,
					UserAccessValidationState.VALIDATION_SUCCESS_GENERAL_ACCESS,
					UserAccessValidationState.LOADING_USER_PERMISSIONS,
				] as UserAccessValidationState[]
			).includes(userAccessState) &&
				isValidFastshiftRoute(pathname) &&
				strippedPathname !== paths.home,
		);
	}, [userAccessState, isValidFastshiftRoute, pathname, strippedPathname, paths.home]);

	const isOnHomeRoute: boolean = useMemo(
		() => strippedPathname === paths.home,
		[strippedPathname, paths.home],
	);

	const currentRouteKey = useMemo(
		() => getRouteKeyForPathname(strippedPathname, paths),
		[paths, strippedPathname],
	);

	const currentRouteRequiresMoveTicketAccess =
		isGeneralAccessEnabled && currentRouteKey
			? routeRequiresMoveTicketAccess(currentRouteKey)
			: false;

	const userCanAccessCurrentRoutePolicyByUserType =
		isGeneralAccessEnabled && currentRouteKey
			? canUserAccessRoutePolicyByUserType(FASTSHIFT_ROUTE_ACCESS_POLICIES[currentRouteKey], user)
			: false;

	const customerConsentCheckStillPending =
		(userIsPending || user?.userType === 'CUSTOMER') && userHasConsentedIsPending;

	const resolvesIfUserIsAuthenticated = useCallback(async () => {
		return stargateClient.me({});
	}, [stargateClient]);

	const redirectToHomeRoute = () => {
		if (strippedPathname !== paths.home) {
			navigate(paths.home);
		}
	};

	const redirectIneligibleUserToHome = () => {
		setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_INELIGIBLE_FOR_PORTAL);
		redirectToHomeRoute();
	};

	const redirectUserToConsentLandingPage = () => {
		setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_CUSTOMER_NOT_CONSENTED);
		redirectToHomeRoute();
	};

	// Trigger appropriate checks on page route change
	useEffect(() => {
		// if present, user has already passed portal eligibility checks
		if (user) {
			// Avoid flickering/loading state when navigating between routes after success
			if (userAccessState !== UserAccessValidationState.VALIDATION_SUCCESS) {
				setUserAccessState(UserAccessValidationState.LOADING_USER_PERMISSIONS);
			}
			return;
		}

		// if previously shown to be ineligible for portal
		if (userError) {
			redirectIneligibleUserToHome();
			return;
		}

		setUserAccessState(UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY);
		// should only run when user changes page
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname]);

	// Portal Eligibility: Check User is Authenticated
	useEffect(() => {
		if (userAccessState !== UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY) {
			return;
		}

		// if user details is not present, ensure user is authenticated and fetch details
		resolvesIfUserIsAuthenticated()
			// user is authenticated, so now we can check eligibility
			.then(() => {
				if (isGeneralAccessEnabled) {
					// Retrieve /user/access details
					dispatch(StateActions.getUserAccess(stargateClient));
				} else {
					// Retrieve /user details
					dispatch(StateActions.getUserDetails(stargateClient));
				}
				dispatch(
					StateActions.getPortalUserConsent({
						stargateClient,
						displayedText: CONSENT_PAGE_LEGAL_DISPLAY_TEXT_VERSION,
					}),
				);
			})
			.catch(() => {
				// Redirect to Identity to login user
				const currentDomain = window.location.hostname;
				// If fastshift domain - use new fastshift login auth redirect url
				if (FASTSHIFT_DOMAINS.includes(currentDomain)) {
					const fastshiftLoginAuthContinueUrl = encodeURIComponent(
						`${FASTSHIFT_BASE_URL}${FASTSHIFT_AUTH_REDIRECT_URL}`,
					);
					const loginUrl = `${process.env['NEXT_PUBLIC_IDENTITY_BASE_URL']}/login?continue=${fastshiftLoginAuthContinueUrl}`;
					window.location.href = loginUrl;
				} else {
					const loginUrl = `${
						process.env['NEXT_PUBLIC_IDENTITY_BASE_URL']
					}/login?continue=${encodeURIComponent(`${BASE_URL}${strippedPathname}`)}`;
					window.location.href = loginUrl;
				}
			});
		// should only run Portal Eligibility check is initiated
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAccessState]);

	// Portal Eligibility: Check User is Eligible
	useEffect(() => {
		if (userAccessState !== UserAccessValidationState.LOADING_PORTAL_ELIGIBILITY) {
			return;
		}

		// still resolving details
		if (!user && !userError) {
			return;
		}

		// if ineligible for portal
		if (userError) {
			redirectIneligibleUserToHome();
			return;
		}

		// wait for check if customer has consented
		if (customerConsentCheckStillPending) {
			return;
		}

		// if customer hasn't consented, redirect to home
		if (!userHasConsented && user?.userType === 'CUSTOMER') {
			redirectUserToConsentLandingPage();
			return;
		}

		// at this point, permissions on specific page may be checked
		setUserAccessState(UserAccessValidationState.LOADING_USER_PERMISSIONS);

		// if on EAP home route, redirect to Milestone 2 home route
		if (strippedPathname === paths.eapHome) {
			redirectToHomeRoute();
			return;
		}

		// should only run when /user endpoint resolves during eligibility check
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user, userError, customerConsentCheckStillPending]);

	// User Permissions: Page Check
	// eslint-disable-next-line complexity
	useEffect(() => {
		// Wait until portal eligibility (user details) is resolved
		if (!user && !userError) {
			return;
		}

		// If user is ineligible for portal, do not perform route-level checks
		if (userError) {
			return;
		}

		// wait for check if customer has consented
		if (customerConsentCheckStillPending) {
			return;
		}

		// any authenticated user may access home /fastshift route
		if (strippedPathname === paths.home) {
			setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
			return;
		}

		// if not on a valid fastshift route, immediate 404
		if (!isValidFastshiftRoute(strippedPathname)) {
			setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE);
			return;
		}

		// check partner-specific routes (no domainId)
		if (!domainId) {
			if (isPartnerOrInternal && isValidFastshiftPartnerRoute(strippedPathname)) {
				setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
			} else {
				setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE);
			}
			return;
		}

		// move-ticket routes require explicit move ticket access, even if the domain is otherwise accessible
		if (currentRouteRequiresMoveTicketAccess && !user?.hasMoveTicketAccess) {
			setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE);
			return;
		}

		// general-access route is valid for the user type, but MOVE-backed data remains unavailable
		if (
			userCanAccessCurrentRoutePolicyByUserType &&
			!currentRouteRequiresMoveTicketAccess &&
			user?.hasMoveTicketAccess === false
		) {
			if (pageDomainIsPresentInPartnerSelectionTable || domainId === user.userDomain.domainId) {
				setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS_GENERAL_ACCESS);
				return;
			} else {
				setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE);
				return;
			}
		}

		// domain-scoped routes:
		// customer may access their own page without waiting for domain API
		if (user?.userType === 'CUSTOMER' && domainId === user.userDomain.domainId) {
			setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
			return;
		}

		// partners/internal may access domains from selection table
		if (isPartnerOrInternal) {
			if (pageDomainIsPresentInPartnerSelectionTable === true) {
				setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
				return;
			}
		}

		// still pending domain info -> keep current state (likely LOADING_USER_PERMISSIONS)
		if (!customerDomainData && !customerDomainError) {
			return;
		}

		// if /domain returns error, then user may not access page
		if (customerDomainError) {
			setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE);
			return;
		}

		// any user may access a page where /domain returns valid response
		if (customerDomainData && customerDomainData.customerDomainId === domainId) {
			setUserAccessState(UserAccessValidationState.VALIDATION_SUCCESS);
			return;
		}

		// default: no access
		setUserAccessState(UserAccessValidationState.VALIDATION_FAIL_NO_ACCESS_TO_SELECTED_ROUTE);
	}, [
		strippedPathname,
		domainId,
		user,
		customerDomainData,
		customerDomainError,
		userError,
		isPartnerOrInternal,
		isValidFastshiftRoute,
		isValidFastshiftPartnerRoute,
		paths,
		pageDomainIsPresentInPartnerSelectionTable,
		customerConsentCheckStillPending,
		currentRouteRequiresMoveTicketAccess,
		userCanAccessCurrentRoutePolicyByUserType,
	]);

	return {
		userAccessState,
		shouldShowSideNav,
		isPartnerOrInternal,
		isOnHomeRoute,
	};
};
